// ============================================================================
// CANCEL BOOKING CONFIRM DIALOG
// ============================================================================
// Replaces the legacy "Delete" row-action with a proper status transition to
// CANCELLED. Captures the mandatory reason and the operator's manual
// charge/refund disposition (Phase 1 — full days-based policy comes with the
// dedicated TXN-CANC sprint per CRS-Feature-Specification §4.8).

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { cancellationService } from 'api/services/cancellationService';
import type { Booking } from 'api/types/booking.types';
import notificationService from 'services/notificationService';
import { SyutSelect } from 'components/ui';

interface CancelBookingConfirmDialogProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onCancelled: () => void;
}

const REFUND_MODE_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Card', label: 'Credit / Debit Card' },
  { value: 'UPI', label: 'UPI' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
  { value: 'Credit Note', label: 'Credit Note' },
  { value: 'Original Payment Mode', label: 'Original Payment Mode' },
];

const formatCurrency = (n: number) => `₹${(n ?? 0).toFixed(2)}`;

const CancelBookingConfirmDialog: React.FC<CancelBookingConfirmDialogProps> = ({
  isOpen, booking, onClose, onCancelled,
}) => {
  const [reason, setReason] = useState('');
  const [cancellationCharge, setCancellationCharge] = useState<string>('0');
  const [refundAmount, setRefundAmount] = useState<string>('0');
  const [refundMode, setRefundMode] = useState<string>('');
  const [refundReference, setRefundReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const advanceReceived = booking?.advanceReceived ?? 0;

  // Pre-fill refund amount with the advance received when the dialog opens.
  // Operator can adjust before confirming.
  useEffect(() => {
    if (isOpen && booking) {
      setReason('');
      setCancellationCharge('0');
      setRefundAmount(advanceReceived > 0 ? advanceReceived.toFixed(2) : '0');
      setRefundMode('');
      setRefundReference('');
      setTouched(false);
    }
  }, [isOpen, booking, advanceReceived]);

  const trimmedReason = reason.trim();
  const parsedCharge = Number.parseFloat(cancellationCharge || '0');
  const parsedRefund = Number.parseFloat(refundAmount || '0');

  const reasonError = useMemo(() => {
    if (trimmedReason.length < 3) return 'Reason is required (at least 3 characters).';
    if (trimmedReason.length > 500) return 'Reason must be 500 characters or fewer.';
    return '';
  }, [trimmedReason]);

  const chargeError = useMemo(() => {
    if (Number.isNaN(parsedCharge)) return 'Enter a valid amount.';
    if (parsedCharge < 0) return 'Charge cannot be negative.';
    return '';
  }, [parsedCharge]);

  const refundError = useMemo(() => {
    if (Number.isNaN(parsedRefund)) return 'Enter a valid amount.';
    if (parsedRefund < 0) return 'Refund cannot be negative.';
    if (parsedRefund > advanceReceived + 0.0001) {
      return `Refund cannot exceed the advance received (${formatCurrency(advanceReceived)}).`;
    }
    return '';
  }, [parsedRefund, advanceReceived]);

  const refundModeError = useMemo(() => {
    if (parsedRefund > 0 && !refundMode.trim()) {
      return 'Select a refund mode for the entered refund amount.';
    }
    return '';
  }, [parsedRefund, refundMode]);

  const hasErrors = !!(reasonError || chargeError || refundError || refundModeError);

  if (!isOpen || !booking) return null;

  const handleConfirm = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSubmitting(true);
    try {
      await cancellationService.cancel(booking.bookingId, {
        reason: trimmedReason,
        cancellationCharge: parsedCharge,
        refundAmount: parsedRefund,
        refundMode: refundMode.trim() || undefined,
        refundReference: refundReference.trim() || undefined,
      });
      notificationService.toast.success(`Booking "${booking.bookingNumber}" cancelled.`);
      onCancelled();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]
        || err?.message
        || 'Failed to cancel booking.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={submitting ? undefined : onClose} />
      <div className="modal-container modal-md">
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#ef4444', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Cancelling…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Cancel Booking</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Booking summary */}
          <div style={{
            padding: '0.75rem 0.875rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            marginBottom: '0.875rem',
            fontSize: '0.85rem',
          }}>
            <div><strong>Booking:</strong> {booking.bookingNumber}</div>
            <div><strong>Guest:</strong> {booking.guestFullName || '—'}</div>
            <div><strong>Status:</strong> {booking.bookingStatus || booking.bookingStatusCode}</div>
            <div><strong>Check-In:</strong> {booking.checkInDate?.slice(0, 10)}</div>
            <div><strong>Advance Received:</strong> {formatCurrency(advanceReceived)}</div>
            <div style={{
              marginTop: '0.5rem',
              padding: '0.5rem 0.625rem',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '4px',
              color: 'var(--text-primary)',
              fontSize: '0.8125rem',
            }}>
              Cancelling releases the room inventory and transitions the booking
              to <strong>CANCELLED</strong>. The cancellation reason and refund
              disposition are recorded in the audit log and the cancellation register.
              This cannot be reversed.
            </div>
          </div>

          {/* Reason */}
          <div className="form-group">
            <label className="form-label">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-textarea${touched && reasonError ? ' form-field-error' : ''}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Guest requested cancellation — flight cancelled."
              style={{ resize: 'vertical', minHeight: '70px' }}
              disabled={submitting}
            />
            {touched && reasonError && <div className="form-error-message">{reasonError}</div>}
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              {trimmedReason.length}/500 characters
            </div>
          </div>

          {/* Charge + Refund grid */}
          <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
            <div className="form-group">
              <label className="form-label">Cancellation Charge (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`form-input${touched && chargeError ? ' form-field-error' : ''}`}
                value={cancellationCharge}
                onChange={(e) => setCancellationCharge(e.target.value)}
                onBlur={() => setTouched(true)}
                disabled={submitting}
              />
              {touched && chargeError && <div className="form-error-message">{chargeError}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Refund Amount (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`form-input${touched && refundError ? ' form-field-error' : ''}`}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                onBlur={() => setTouched(true)}
                disabled={submitting}
              />
              {touched && refundError && <div className="form-error-message">{refundError}</div>}
            </div>
          </div>

          {/* Refund mode + reference (only meaningful when refund > 0) */}
          {parsedRefund > 0 && (
            <div className="form-grid-2" style={{ marginTop: '0.25rem' }}>
              <div className="form-group">
                <SyutSelect
                  label={<>Refund Mode <span className="text-danger">*</span></>}
                  value={refundMode}
                  onChange={(v) => setRefundMode((v as string) || '')}
                  options={REFUND_MODE_OPTIONS}
                  isClearable
                  error={touched ? refundModeError : undefined}
                  size="sm"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Refund Reference</label>
                <input
                  type="text"
                  maxLength={100}
                  className="form-input"
                  value={refundReference}
                  onChange={(e) => setRefundReference(e.target.value)}
                  placeholder="Txn id / cheque no."
                  disabled={submitting}
                />
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Close
          </button>
          <button
            className="btn btn-danger"
            onClick={handleConfirm}
            disabled={submitting || (touched && hasErrors)}
          >
            Cancel Booking
          </button>
        </div>
      </div>
    </>
  );
};

export default CancelBookingConfirmDialog;
